/**
 * The CLOSED v1 ABAC condition vocabulary and builder model (OPSFL-FSD-IAM-UC2 §6) — the SPA-side
 * mirror of `platform-security/.../rbac/AbacAttributes.java` and the `AbacEvaluator` publish
 * validation. Mirrored, not imported (Java constants are unreachable here); the server refuses at
 * publish and DENIES at evaluation regardless — this module only decides what the builder OFFERS
 * and what the pre-flight check teaches. The vocabulary is deliberately literal: 15 paths, 5 ops,
 * depth ≤ 3, ≤ 20 predicates, literal-only values, no direct identifiers.
 */

export interface AbacPathMeta {
  path: string;
  /** Where the value comes from — the resolver contract, said plainly. */
  blurb: string;
  /** Collection-valued paths match if ANY element matches (eq/in are membership tests). */
  collection: boolean;
}

/** The 15 registry paths (SUBJECT ∪ RESOURCE ∪ ENV) — a policy path outside this set denies. */
export const ABAC_PATHS: readonly AbacPathMeta[] = [
  // subject.* — from the bound identity (+ the request's effective-authority reads)
  { path: 'subject.roles', blurb: 'The caller’s catalogue roles.', collection: true },
  { path: 'subject.customRoles', blurb: 'The caller’s x_ custom roles.', collection: true },
  { path: 'subject.spokeId', blurb: 'The caller’s spoke binding (absent = hub scope).', collection: false },
  { path: 'subject.hasOversight', blurb: 'Whether the caller holds the oversight lane (true/false).', collection: false },
  { path: 'subject.projects', blurb: 'Projects the caller is a live member of.', collection: true },
  { path: 'subject.projectLeadOf', blurb: 'Projects the caller LEADS.', collection: true },
  // resource.* — ONLY from the resource's own hub-schema row (no cross-store hop on the authz path)
  { path: 'resource.type', blurb: 'The resource type (work_item, flow, entity, dataroom_invite).', collection: false },
  { path: 'resource.spokeId', blurb: 'The spoke the resource belongs to.', collection: false },
  { path: 'resource.status', blurb: 'The resource’s own status value.', collection: false },
  { path: 'resource.priority', blurb: 'The resource’s priority value.', collection: false },
  { path: 'resource.itemType', blurb: 'The work item’s type.', collection: false },
  { path: 'resource.projectIds', blurb: 'Projects the resource is linked to.', collection: true },
  { path: 'resource.restricted', blurb: 'Whether the resource is marked restricted (true/false).', collection: false },
  // env.* — the request clock, UTC BY NAME (preventing the silent-wrong-timezone class)
  { path: 'env.utcHour', blurb: 'The request hour, 0–23, UTC by name.', collection: false },
  { path: 'env.utcDayOfWeek', blurb: 'The request day, MONDAY…SUNDAY, UTC by name.', collection: false },
];

export const ABAC_PATH_SET: ReadonlySet<string> = new Set(ABAC_PATHS.map((p) => p.path));

/** The five operators — no ordering ops (no numeric attribute exists in v1). */
export const ABAC_OPS = ['eq', 'ne', 'in', 'nin', 'exists'] as const;
export type AbacOp = (typeof ABAC_OPS)[number];

export const ABAC_MAX_DEPTH = 3;
export const ABAC_MAX_PREDICATES = 20;

/** A 26-char Crockford-ish token — the structural direct-identifier ban's target shape. */
const ULID_SHAPE = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/**
 * The builder's typed model. One root combinator over items, where an item is a predicate or ONE
 * nested group of predicates — exactly the grammar's legal envelope (root=1, group=2, leaf=3,
 * MAX_DEPTH=3), so nothing legal exists that the builder cannot express (the FTM
 * dual-representation rule), and nothing the builder emits can exceed the caps by shape.
 */
export interface AbacPredicate {
  kind: 'predicate';
  path: string;
  op: AbacOp;
  /** eq/ne: one literal; in/nin: literal list; exists: boolean. */
  value: string | string[] | boolean;
}

export interface AbacGroup {
  kind: 'group';
  combine: 'all' | 'any';
  predicates: AbacPredicate[];
}

export interface AbacConditionModel {
  combine: 'all' | 'any';
  items: (AbacPredicate | AbacGroup)[];
}

export function emptyCondition(): AbacConditionModel {
  return { combine: 'all', items: [emptyPredicate()] };
}

export function emptyPredicate(): AbacPredicate {
  return { kind: 'predicate', path: 'subject.spokeId', op: 'eq', value: '' };
}

// ---- model → JSON -----------------------------------------------------------------------------

function predicateJson(p: AbacPredicate): unknown {
  if (p.op === 'exists') return { path: p.path, op: p.op, value: p.value === true };
  if (p.op === 'in' || p.op === 'nin') {
    return { path: p.path, op: p.op, value: Array.isArray(p.value) ? p.value : [] };
  }
  return { path: p.path, op: p.op, value: typeof p.value === 'string' ? p.value : String(p.value) };
}

/**
 * The canonical wire condition. A single bare predicate collapses to the predicate object itself
 * (no needless one-arm composite); anything else is `{all|any: [...]}`.
 */
export function conditionToJson(model: AbacConditionModel): unknown {
  const items = model.items.map((item) =>
    item.kind === 'predicate'
      ? predicateJson(item)
      : { [item.combine]: item.predicates.map(predicateJson) });
  if (items.length === 1 && model.items[0]?.kind === 'predicate') return items[0];
  return { [model.combine]: items };
}

// ---- JSON → model -----------------------------------------------------------------------------

type Rec = Record<string, unknown>;

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parsePredicate(node: Rec): AbacPredicate | null {
  const path = node.path;
  const op = node.op;
  if (typeof path !== 'string' || !ABAC_PATH_SET.has(path)) return null;
  if (typeof op !== 'string' || !(ABAC_OPS as readonly string[]).includes(op)) return null;
  const value = node.value;
  if (op === 'exists') {
    return typeof value === 'boolean' ? { kind: 'predicate', path, op, value } : null;
  }
  if (op === 'in' || op === 'nin') {
    if (!Array.isArray(value) || value.some((v) => isRec(v) || Array.isArray(v))) return null;
    return { kind: 'predicate', path, op, value: value.map((v) => String(v)) };
  }
  if (value === undefined || value === null || isRec(value) || Array.isArray(value)) return null;
  return { kind: 'predicate', path, op: op as AbacOp, value: String(value) };
}

/**
 * Parse a wire condition back into the builder model. Returns null when the JSON is not a LEGAL
 * grammar shape over the closed vocabulary (unknown path/op, wrong value shape, nesting beyond the
 * legal envelope) — the caller keeps the JSON tab authoritative and says why the builder stands
 * aside, instead of silently rewriting the condition.
 */
export function jsonToCondition(json: unknown): AbacConditionModel | null {
  if (!isRec(json)) return null;
  const rootKey = 'all' in json ? 'all' : 'any' in json ? 'any' : null;
  if (rootKey === null) {
    const p = parsePredicate(json);
    return p ? { combine: 'all', items: [p] } : null;
  }
  const children = json[rootKey];
  if (!Array.isArray(children) || children.length === 0) return null;
  const items: (AbacPredicate | AbacGroup)[] = [];
  for (const child of children) {
    if (!isRec(child)) return null;
    const childKey = 'all' in child ? 'all' : 'any' in child ? 'any' : null;
    if (childKey === null) {
      const p = parsePredicate(child);
      if (!p) return null;
      items.push(p);
    } else {
      const grandchildren = child[childKey];
      if (!Array.isArray(grandchildren) || grandchildren.length === 0) return null;
      const predicates: AbacPredicate[] = [];
      for (const g of grandchildren) {
        if (!isRec(g)) return null;
        const p = parsePredicate(g); // depth 3 is the leaf floor — a nested group here is illegal
        if (!p) return null;
        predicates.push(p);
      }
      items.push({ kind: 'group', combine: childKey, predicates });
    }
  }
  return { combine: rootKey, items };
}

// ---- pre-flight validation (mirror of AbacEvaluator.validateForPublish) -----------------------

function countAndValidate(node: unknown, problems: string[], depth: number): number {
  if (!isRec(node)) {
    problems.push('condition node must be an object');
    return 0;
  }
  if (depth > ABAC_MAX_DEPTH) {
    problems.push(`condition exceeds depth ${ABAC_MAX_DEPTH}`);
    return 0;
  }
  if ('all' in node || 'any' in node) {
    const children = node['all' in node ? 'all' : 'any'];
    if (!Array.isArray(children) || children.length === 0) {
      problems.push("'all'/'any' must be a non-empty array");
      return 0;
    }
    return children.reduce<number>((n, c) => n + countAndValidate(c, problems, depth + 1), 0);
  }
  const path = node.path;
  const op = node.op;
  const value = node.value;
  if (typeof path !== 'string' || !ABAC_PATH_SET.has(path)) {
    problems.push(`unknown attribute path ${typeof path === 'string' ? `'${path}'` : '(missing)'} — the registry is closed`);
    return 1;
  }
  if (typeof op !== 'string' || !(ABAC_OPS as readonly string[]).includes(op)) {
    problems.push(`unknown op ${typeof op === 'string' ? `'${op}'` : '(missing)'}`);
    return 1;
  }
  if (op === 'exists') {
    if (typeof value !== 'boolean') problems.push("'exists' takes a boolean value");
    return 1;
  }
  const banIdentifier = (literal: unknown) => {
    if (typeof literal === 'string' && ULID_SHAPE.test(literal)) {
      problems.push(`literal '${literal}' is a direct identifier — instance decisions belong in the ACL, not in a policy`);
    }
  };
  if (op === 'eq' || op === 'ne') {
    if (value === undefined || value === null || isRec(value) || Array.isArray(value)) {
      problems.push(`'${op}' takes a single literal value`);
    } else {
      banIdentifier(value);
    }
    return 1;
  }
  // in | nin
  if (!Array.isArray(value) || value.length === 0) {
    problems.push(`'${op}' takes a non-empty literal list`);
    return 1;
  }
  for (const candidate of value) {
    if (isRec(candidate) || Array.isArray(candidate)) {
      problems.push(`'${op}' list values must be literals`);
    } else {
      banIdentifier(candidate);
    }
  }
  return 1;
}

/**
 * Pre-flight problems for a wire condition (empty = passes the mirror of the server's publish
 * validation). Teaching only — the SERVER validation at publish is the gate, and an evaluator
 * that cannot resolve anything still denies.
 */
export function validateCondition(json: unknown): string[] {
  const problems: string[] = [];
  const predicates = countAndValidate(json, problems, 1);
  if (predicates > ABAC_MAX_PREDICATES) {
    problems.push(`condition carries ${predicates} predicates (max ${ABAC_MAX_PREDICATES})`);
  }
  if (predicates === 0 && problems.length === 0) problems.push('condition carries no predicate');
  return problems;
}
